export interface MarginCharge {
  loadId: string;
  side: "customer" | "carrier";
  amountCents: number;
}

export interface MarginLoad {
  id: string;
  customerName: string;
  carrierName?: string;
  origin: { state: string };
  destination: { state: string };
}

export interface MarginReportRow {
  key: string;
  revenueCents: number;
  costCents: number;
  marginCents: number;
  marginPct: number;
  loadCount: number;
}

export function buildMarginReport(loads: MarginLoad[], charges: MarginCharge[], groupBy: "customer" | "lane" | "carrier") {
  const chargeByLoad = new Map<string, { revenueCents: number; costCents: number }>();
  for (const charge of charges) {
    const current = chargeByLoad.get(charge.loadId) ?? { revenueCents: 0, costCents: 0 };
    if (charge.side === "customer") current.revenueCents += charge.amountCents;
    else current.costCents += charge.amountCents;
    chargeByLoad.set(charge.loadId, current);
  }

  const rows = new Map<string, MarginReportRow>();
  for (const load of loads) {
    const key = keyFor(load, groupBy);
    const totals = chargeByLoad.get(load.id) ?? { revenueCents: 0, costCents: 0 };
    const row =
      rows.get(key) ??
      ({
        key,
        revenueCents: 0,
        costCents: 0,
        marginCents: 0,
        marginPct: 0,
        loadCount: 0,
      } satisfies MarginReportRow);
    row.revenueCents += totals.revenueCents;
    row.costCents += totals.costCents;
    row.marginCents = row.revenueCents - row.costCents;
    row.marginPct = row.revenueCents === 0 ? 0 : Math.round((row.marginCents / row.revenueCents) * 1000) / 10;
    row.loadCount += 1;
    rows.set(key, row);
  }
  return [...rows.values()].sort((left, right) => right.marginCents - left.marginCents);
}

function keyFor(load: MarginLoad, groupBy: "customer" | "lane" | "carrier") {
  if (groupBy === "customer") return load.customerName;
  if (groupBy === "carrier") return load.carrierName ?? "Unassigned";
  return `${load.origin.state}-${load.destination.state}`;
}
